import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withToastManager } from 'react-toast-notifications';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { nsOptions } from '../i18n';
import { childrenPropTypes } from '../utils/generic-prop-types';
import TextUtil from '../utils/TextUtil';
import NewTooltip from './NewTooltip';
import ElementUtil from '../utils/ElementUtil';
import SortUtil from '../utils/SortUtil';

const NAME_MAX_LENGTH = 40;

@withTranslation('', nsOptions)
class DataItem extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    pageId: PropTypes.number.isRequired,
    element: PropTypes.shape().isRequired,
    projectElement: PropTypes.shape().isRequired,
    moduleId: PropTypes.number,
    moduleInstanceId: PropTypes.number,
    handleClick: PropTypes.func.isRequired,
    children: childrenPropTypes(),
    extraInfo: PropTypes.string,
    showConstrainingViolation: PropTypes.bool,
  };

  static defaultProps = {
    moduleId: null,
    moduleInstanceId: null,
    children: undefined,
    extraInfo: null,
    showConstrainingViolation: false,
  }

  render() {
    const {
      t, element, projectElement, pageId, handleClick, children, moduleId,
      moduleInstanceId, extraInfo, showConstrainingViolation,
    } = this.props;

    const elementName = ElementUtil.formatElementName(element, t);

    const isConstraining = showConstrainingViolation && element.is_constraining;

    return (
      <li key={`${moduleInstanceId ? `entry-${moduleInstanceId}-` : ''}list-item-${projectElement.id}`}>
        {isConstraining && (
          <NewTooltip
            content={t('inclusion:constraining-violation-tooltip')}
          >
            <FontAwesomeIcon
              icon={['fas', 'exclamation-circle']}
              className="text-danger mr-1"
              size="sm"
            />
          </NewTooltip>
        )}
        {extraInfo && (
          <NewTooltip
            contentClassName="small"
            content={t(`inclusion:qualify-missing-data.${extraInfo.replace('_', '-')}`)}
          >
            <span className="font-weight-bold text-newblue-1 mr-1">
              [
              {t(`inclusion:qualified-missing-data.${extraInfo.replace('_', '-')}-code`)}
              ]
            </span>
          </NewTooltip>
        )}
        <NewTooltip
          contentClassName="small"
          content={elementName}
          disabled={!(elementName.length > NAME_MAX_LENGTH)}
        >
          <a
            key={`${moduleInstanceId ? `entry-${moduleInstanceId}-` : ''}link-${projectElement.id}`}
            role="button"
            tabIndex="-1"
            onClick={() => handleClick(pageId, projectElement.id, moduleId, moduleInstanceId)}
            onKeyPress={() => {}}
      // Next line allows to handle link hover (otherwise bootstrap overwrites the hover
      // behaviour)
            className={isConstraining ? 'text-danger font-weight-bold' : ''}
          >
            {TextUtil.truncate(elementName, NAME_MAX_LENGTH)}
          </a>
        </NewTooltip>
        {children}
      </li>
    );
  }
}

const mapStateToProps = (state) => ({
  projectPages: state.projectPages,
});

const mapDispatchToProps = () => ({
});

@withToastManager
@connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })
@withTranslation('', nsOptions)
class DataMonitoringTooltip extends Component {
  static propTypes = {
    projectPages: PropTypes.shape().isRequired,
    projectElements: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    elements: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    projectEntries: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    formattedResult: PropTypes.shape().isRequired,
    formattedOptionalResult: PropTypes.shape(),
    handleClick: PropTypes.func.isRequired,
    totalPrjElements: PropTypes.number.isRequired,
    displayOptionalResult: PropTypes.bool,
    title: PropTypes.string.isRequired,
    nothingToDisplayMessage: PropTypes.string.isRequired,
    extraInfo: PropTypes.string,
    mainTabName: PropTypes.string,
    secondTabName: PropTypes.string,
    secondTabNote: PropTypes.string,
    // DQ-222: optional 3rd tab for NA/ND
    thirdTabName: PropTypes.string,
    formattedThirdResult: PropTypes.shape(),
    footer: PropTypes.shape(),
    showConstrainingViolation: PropTypes.bool,
  };

  static defaultProps = {
    formattedOptionalResult: null,
    displayOptionalResult: false,
    extraInfo: null,
    mainTabName: null,
    secondTabName: null,
    secondTabNote: null,
    thirdTabName: null,
    formattedThirdResult: null,
    footer: undefined,
    showConstrainingViolation: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      activeTab: 'main', // 'main' | 'second' | 'third'
    };
  }

  countItems = (sourceData) => {
    if (!sourceData) return 0;
    let count = 0;
    Object.keys(sourceData).forEach((page) => {
      Object.values(sourceData[page]).forEach((item) => {
        if (item.type === 'variable') {
          count += 1;
        } else {
          Object.values(item).forEach((entry) => {
            Object.values(entry).forEach((elems) => {
              count += elems.length;
            });
          });
        }
      });
    });
    return count;
  }

  getFormattedContent = (pageId, contentToDisplay) => {
    const {
      projectElements, elements, projectEntries, handleClick, extraInfo,
      showConstrainingViolation,
    } = this.props;
    const pageData = contentToDisplay[pageId];
    const res = Object.values(pageData).map((data) => {
      if (data.type === 'variable') {
        const prjElement = projectElements.find((pEl) => pEl.id === data.id);
        const element = elements.find((el) => el.id === prjElement.element);
        return (
          <DataItem
            pageId={pageId}
            projectElement={prjElement}
            element={element}
            handleClick={handleClick}
            key={prjElement.id}
            extraInfo={data[extraInfo]}
            showConstrainingViolation={showConstrainingViolation}
          />
        );
      }
      const moduleId = data.id;
      const modulePrjElement = projectElements.find((pEl) => pEl.id === moduleId);
      const element = elements.find((el) => el.id === modulePrjElement.element);
      return (
        <span key={`entries-module-${moduleId}`}>
          <DataItem
            pageId={pageId}
            projectElement={modulePrjElement}
            element={element}
            handleClick={handleClick}
            key={modulePrjElement.id}
            extraInfo={data[extraInfo]}
            showConstrainingViolation={showConstrainingViolation}
          >
            <ul
              className="data-monitoring-list"
              key={`entries-list-module-${modulePrjElement.id}`}
            >
              {
                Object.values(data.entries).map((entry) => {
                  const projectEntry = projectEntries.find((pEntry) => pEntry.id === entry.id);
                  return (
                    <span key={`content-entry-${entry.id}`}>
                      <NewTooltip
                        contentClassName="small"
                        content={projectEntry.value}
                        disabled={!(projectEntry.value.length > NAME_MAX_LENGTH)}
                      >
                        <li
                          key={`entry-${entry.id}`}
                        >
                          <a
                            role="button"
                            tabIndex="-1"
                            onClick={() => handleClick(pageId, null, moduleId, entry.id)}
                            onKeyPress={() => {}}
                            className=""
                          >
                            {TextUtil.truncate(projectEntry.value, NAME_MAX_LENGTH)}
                          </a>
                        </li>
                      </NewTooltip>
                      <ul
                        className="data-monitoring-list"
                        key={`list-entry-${entry.id}`}
                      >
                        {
                          Object.values(entry.childs).map((pElement) => {
                            const projectElement = projectElements.find((pEl) => (
                              pEl.id === pElement.id));
                            const elementInModule = elements.find((el) => (
                              el.id === projectElement.element));
                            return (
                              <DataItem
                                pageId={pageId}
                                projectElement={projectElement}
                                element={elementInModule}
                                moduleId={modulePrjElement.id}
                                moduleInstanceId={entry.id}
                                handleClick={handleClick}
                                key={`${entry}-${projectElement.id}`}
                                extraInfo={pElement[extraInfo]}
                                showConstrainingViolation={showConstrainingViolation}
                              />
                            );
                          })
                        }
                      </ul>
                    </span>
                  );
                })
              }
            </ul>
          </DataItem>
        </span>
      );
    });
    return res;
  }

  render() {
    const {
      projectPages, handleClick, displayOptionalResult, formattedResult,
      formattedOptionalResult, totalPrjElements, title, nothingToDisplayMessage,
      mainTabName, secondTabName, secondTabNote, thirdTabName, formattedThirdResult,
      footer,
    } = this.props;
    const { activeTab } = this.state;

    // eslint-disable-next-line no-nested-ternary
    const contentToDisplay = activeTab === 'third' ? (formattedThirdResult || {})
      : (activeTab === 'second' ? formattedOptionalResult : formattedResult);

    let totalPrjElementsToDisplay = totalPrjElements;
    if (activeTab !== 'main') {
      totalPrjElementsToDisplay = 0;
      const sourceData = activeTab === 'third'
        ? (formattedThirdResult || {}) : formattedOptionalResult;
      Object.keys(sourceData || {}).forEach((page) => {
        Object.values(sourceData[page]).forEach((item) => {
          if (item.type === 'variable') {
            totalPrjElementsToDisplay += 1;
          } else {
            Object.values(item).forEach((entry) => {
              Object.values(entry).forEach((elements) => {
                totalPrjElementsToDisplay += elements.length;
              });
            });
          }
        });
      });
    }

    const pagesIds = Object.keys(contentToDisplay).map((pageId) => (
      Number.parseFloat(pageId)));
    const pages = Object.values(projectPages).filter((projectPage) => (
      pagesIds.includes(projectPage.id))).sort(SortUtil.sortArray);
    const mainCount = totalPrjElements;
    const secondCount = this.countItems(formattedOptionalResult);
    const thirdCount = this.countItems(formattedThirdResult);

    return (
      <div>
        <div className="row mx-0 pb-2">
          <div className="col-12 px-1">
            <h5 className="text-center">
              {title}
            </h5>
          </div>
        </div>
        {
          (displayOptionalResult || thirdTabName) && mainTabName && secondTabName && (
            <div className="row mb-3 mx-0">
              <div className="col px-1 text-center">
                <a
                  className={`small ${activeTab === 'main' ? 'font-weight-bold' : ''}`}
                  key="main-tab"
                  role="button"
                  onClick={() => this.setState({ activeTab: 'main' })}
                  onKeyPress={() => {}}
                  tabIndex={0}
                  style={{ textDecoration: activeTab === 'main' ? 'underline' : 'none' }}
                >
                  {`${mainTabName} (${mainCount})`}
                </a>
                <a
                  className={`ml-4 small ${activeTab === 'second' ? 'font-weight-bold' : ''}`}
                  key="second-tab"
                  role="button"
                  onClick={() => this.setState({ activeTab: 'second' })}
                  onKeyPress={() => {}}
                  tabIndex={0}
                  style={{ textDecoration: activeTab === 'second' ? 'underline' : 'none' }}
                >
                  {`${secondTabName} (${secondCount})`}
                </a>
                {thirdTabName && (
                  <a
                    className={`ml-4 small ${activeTab === 'third' ? 'font-weight-bold' : ''}`}
                    key="third-tab"
                    role="button"
                    onClick={() => this.setState({ activeTab: 'third' })}
                    onKeyPress={() => {}}
                    tabIndex={0}
                    style={{ textDecoration: activeTab === 'third' ? 'underline' : 'none' }}
                  >
                    {`${thirdTabName} (${thirdCount})`}
                  </a>
                )}
              </div>
            </div>
          )
        }
        {
          activeTab === 'second' && secondTabNote && (
            <div className="row mx-0 mb-3">
              <div className="col px-1">
                <span className="font-italic text-gray small">
                  {secondTabNote}
                </span>
              </div>
            </div>
          )
        }
        {
          totalPrjElementsToDisplay ? (
            <>
              <div className="row mx-0">
                <div className="col px-1 pt-2">
                  {
                    pages.map((page) => (
                      <span key={`content-page-${page.id}`}>
                        <>
                          <NewTooltip
                            contentClassName="small"
                            content={page.title}
                            disabled={!(page.title.length > NAME_MAX_LENGTH)}
                          >
                            <h6 key={`title-page-${page.id}`}>
                              <a
                                key={`link-page-${page.id}`}
                                role="button"
                                tabIndex="-1"
                                onClick={() => handleClick(page.id)}
                                onKeyPress={() => {}}
                                className=""
                              >
                                <strong>
                                  {TextUtil.truncate(page.title, NAME_MAX_LENGTH)}
                                </strong>
                              </a>
                            </h6>
                          </NewTooltip>
                          <ul
                            className="data-monitoring-list"
                            key={`list-page-${page.id}`}
                          >
                            {this.getFormattedContent(page.id, contentToDisplay)}
                          </ul>
                        </>
                      </span>
                    ))
                  }
                </div>
              </div>
              {footer && (
                <div className="px-1 pt-2">
                  {footer}
                </div>
              )}
            </>
          ) : (
            <div className="row mx-0">
              <div className="col px-1 text-center pt-3">
                <p><strong>{nothingToDisplayMessage}</strong></p>
              </div>
            </div>
          )
        }
      </div>
    );
  }
}

export default DataMonitoringTooltip;
